/**
 * Investor Layout Component
 * 
 * Sidebar navigation for the investor portal.
 * Completely separate from the main admin DashboardLayout.
 */
import { useLocation } from "wouter";
import { useInvestorAuth } from "@/hooks/useInvestorAuth";
import {
  LayoutDashboard,
  Calendar,
  TrendingUp,
  BarChart3,
  LogOut,
  Bell,
  ChevronRight,
  Briefcase,
} from "lucide-react";
import { useState } from "react";

const BRAND = {
  bg: "#080d16",
  sidebar: "#0b1220",
  sidebarBorder: "#1a2540",
  card: "#0e1623",
  border: "#1a2540",
  accent: "#3b82f6",
  accentLight: "rgba(59,130,246,0.12)",
  gold: "#f59e0b",
  green: "#10b981",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

const navItems = [
  { icon: LayoutDashboard, label: "Portfolio Overview", path: "/investor/dashboard" },
  { icon: Calendar, label: "Payment Schedule", path: "/investor/schedule" },
  { icon: BarChart3, label: "Cash Flow", path: "/investor/cashflow" },
  { icon: Bell, label: "Notifications", path: "/investor/notifications" },
];

export default function InvestorLayout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { user, logout } = useInvestorAuth({ redirectOnUnauthenticated: true });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const sidebarWidth = sidebarCollapsed ? 72 : 240;

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: BRAND.bg,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          width: sidebarWidth,
          minHeight: "100vh",
          background: BRAND.sidebar,
          borderRight: `1px solid ${BRAND.sidebarBorder}`,
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s ease",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: sidebarCollapsed ? "20px 0" : "20px 20px",
            borderBottom: `1px solid ${BRAND.sidebarBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: sidebarCollapsed ? "center" : "flex-start",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${BRAND.accent}, #1d4ed8)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TrendingUp size={18} color="white" />
          </div>
          {!sidebarCollapsed && (
            <div>
              <div style={{ color: BRAND.text, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                Investor Portal
              </div>
              <div style={{ color: BRAND.textDim, fontSize: 11 }}>UnifiPro.AI</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
          {navItems.map((item) => {
            const isActive = location === item.path || location.startsWith(item.path + "/");
            return (
              <button
                key={item.path}
                onClick={() => setLocation(item.path)}
                title={sidebarCollapsed ? item.label : undefined}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: sidebarCollapsed ? "10px 0" : "10px 12px",
                  borderRadius: 8,
                  border: "none",
                  background: isActive ? BRAND.accentLight : "transparent",
                  color: isActive ? BRAND.accent : BRAND.textMuted,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  marginBottom: 2,
                  transition: "all 0.15s",
                  justifyContent: sidebarCollapsed ? "center" : "flex-start",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)";
                    (e.currentTarget as HTMLButtonElement).style.color = BRAND.text;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                    (e.currentTarget as HTMLButtonElement).style.color = BRAND.textMuted;
                  }
                }}
              >
                <item.icon size={17} style={{ flexShrink: 0 }} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* User / Logout */}
        <div
          style={{
            padding: "12px 8px",
            borderTop: `1px solid ${BRAND.sidebarBorder}`,
          }}
        >
          {!sidebarCollapsed && user && (
            <div
              style={{
                padding: "8px 12px",
                marginBottom: 4,
                borderRadius: 8,
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <div style={{ color: BRAND.text, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                {user.name || user.email}
              </div>
              <div style={{ color: BRAND.textDim, fontSize: 11 }}>{user.email}</div>
            </div>
          )}
          <button
            onClick={logout}
            title={sidebarCollapsed ? "Sign Out" : undefined}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: sidebarCollapsed ? "10px 0" : "10px 12px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              color: BRAND.textDim,
              cursor: "pointer",
              fontSize: 13,
              transition: "all 0.15s",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "rgba(239,68,68,0.08)";
              (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              (e.currentTarget as HTMLButtonElement).style.color = BRAND.textDim;
            }}
          >
            <LogOut size={17} />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>

          {/* Collapse toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              color: BRAND.textDim,
              cursor: "pointer",
              marginTop: 4,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)";
              (e.currentTarget as HTMLButtonElement).style.color = BRAND.text;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              (e.currentTarget as HTMLButtonElement).style.color = BRAND.textDim;
            }}
          >
            <ChevronRight
              size={16}
              style={{
                transform: sidebarCollapsed ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.25s",
              }}
            />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        style={{
          flex: 1,
          minHeight: "100vh",
          overflow: "auto",
          background: BRAND.bg,
        }}
      >
        {children}
      </main>
    </div>
  );
}
